import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Stack,
  Paper,
  Chip,
  Alert,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { badgeFairnessApi } from '../../services/api';
import BadgeFairnessTrends from './BadgeFairnessTrends';
import PerformanceTabBar from './PerformanceTabBar';
import usePageLoadingEffect from '../../hooks/usePageLoadingEffect';

// Badge Fairness admin tab (spec: .claude-product-specs/badge-fairness.md, Phase v1.1.3).
// Renders what /api/admin/v1/badge-fairness/summary returns — all thresholds and
// verdicts are computed server-side so this tab can never disagree with the guardrail.

const STATUS_CHIP_COLOR = {
  green: 'success',
  amber: 'warning',
  red: 'error',
};

const LAYER_CHIP_COLOR = {
  ok: 'success',
  no_data: 'warning',
  underpowered: 'warning',
  breach: 'error',
};

const fmtPct = (v) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);

const GuardrailRow = ({ guardrail, observedShare }) => {
  if (!guardrail) return null;
  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Guardrail
        </Typography>
        <Chip
          label={`Overall: ${guardrail.overall.toUpperCase()}`}
          color={STATUS_CHIP_COLOR[guardrail.overall] || 'default'}
        />
        <Tooltip title={guardrail.ctr?.detail || ''} arrow>
          <Chip
            size="small"
            variant="outlined"
            label={`CTR (layer 1): ${guardrail.ctr?.status}`}
            color={LAYER_CHIP_COLOR[guardrail.ctr?.status] || 'default'}
          />
        </Tooltip>
        <Tooltip title={guardrail.conversion?.detail || ''} arrow>
          <Chip
            size="small"
            variant="outlined"
            label={`Conversion (layer 2): ${guardrail.conversion?.status}`}
            color={LAYER_CHIP_COLOR[guardrail.conversion?.status] || 'default'}
          />
        </Tooltip>
        {observedShare?.['24h'] && (
          <Tooltip
            title="Fairness share of ranking-time contested pools — the ground truth of the running BADGE_FAIRNESS_ARM_SHARE, derived from rank_strategy (never a config mirror)."
            arrow
          >
            <Chip
              size="small"
              variant="outlined"
              label={`Observed arm share: ${fmtPct(observedShare['24h'].fairness_share)} (24h) / ${fmtPct(
                observedShare['7d']?.fairness_share
              )} (7d)`}
            />
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
};

const STRATEGY_HELP = {
  fairness: 'Contested pool ranked by the fairness sort (the treatment arm).',
  random: "Contested pool, control arm, tied bids — the coin-flip. This IS 'control' today.",
  bid: 'Contested pool, control arm, bids differed. ~0 today (uniform bids); a non-zero count means monetized bidding woke up.',
  price: 'Price spread >5% — cheapest-first, outside the badge experiment. Never eligible for fairness or random.',
};

const ArmsTable = ({ arms, strategyBreakdown }) => {
  if (!arms) return null;
  const windows = Object.keys(arms);
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Per-arm performance (seen contested events)
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Seen = the shopper actually selected the size and saw a multi-match carousel (size_selected_events join).
        Impressions alone are potential exposure. Click join is overlay-level (slight upper bound).
        &quot;control&quot; = &apos;random&apos; (+ any &apos;bid&apos;) pools; see the strategy composition below.
      </Typography>
      {strategyBreakdown && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Multi-match pool composition (7d):
          </Typography>
          {['fairness', 'random', 'bid', 'price'].map((s) => (
            <Tooltip key={s} title={STRATEGY_HELP[s]} arrow>
              <Chip
                size="small"
                variant="outlined"
                label={`${s}: ${(strategyBreakdown[s] || 0).toLocaleString()}`}
                color={s === 'bid' && strategyBreakdown[s] > 0 ? 'warning' : 'default'}
              />
            </Tooltip>
          ))}
        </Stack>
      )}
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Window</TableCell>
              <TableCell>Arm</TableCell>
              <TableCell align="right">Seen events</TableCell>
              <TableCell align="right">Clicks</TableCell>
              <TableCell align="right">CTR</TableCell>
              <TableCell align="right">Conversions</TableCell>
              <TableCell align="right">Conv. rate</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {windows.flatMap((w) =>
              ['fairness', 'control'].map((arm) => {
                const s = arms[w]?.[arm];
                const seen = s ? s.seen_events : 0;
                // When the arm ranked pools this week but none have been SEEN yet, a bare 0
                // reads as "broken" next to the ranked chip above. Show the ranked count as
                // context so the funnel stage (ranked -> seen) is legible. Only on the 7d
                // fairness row, where the count matches the 7d composition chip.
                const rankedThisWeek = strategyBreakdown?.fairness || 0;
                const showRankedHint = arm === 'fairness' && w === '7d' && seen === 0 && rankedThisWeek > 0;
                return (
                  <TableRow key={`${w}-${arm}`}>
                    <TableCell>{w}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={arm === 'control' ? 'control (random)' : arm}
                        color={arm === 'fairness' ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {showRankedHint ? (
                        <Tooltip
                          title={`${rankedThisWeek.toLocaleString()} pools were ranked into the fairness arm this week, but no shopper has selected a size on one and seen the carousel yet. Seen events fill in as traffic accumulates — expect this to climb over the next day.`}
                          arrow
                        >
                          <Box component="span" sx={{ color: 'text.secondary', cursor: 'help' }}>
                            0 <Box component="span" sx={{ fontSize: '0.85em' }}>({rankedThisWeek.toLocaleString()} ranked, awaiting views)</Box>
                          </Box>
                        </Tooltip>
                      ) : (
                        seen.toLocaleString()
                      )}
                    </TableCell>
                    <TableCell align="right">{s ? s.clicks.toLocaleString() : 0}</TableCell>
                    <TableCell align="right">{fmtPct(s?.ctr)}</TableCell>
                    <TableCell align="right">{s ? s.conversions.toLocaleString() : 0}</TableCell>
                    <TableCell align="right">{fmtPct(s?.conversion_rate)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const eur = (v) => (v == null ? '—' : `€${v.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

// Balance Control ledger block (spec overlay/balance-control.md §3.1–§3.3): the order ledger the
// throttle decides on, the band, and the write-once stamps ranking reads. Orders are on
// Europe/Amsterdam days (billing's bounds); the click columns above are on UTC days.
const LedgerTable = ({ ledger }) => {
  if (!ledger?.length) return null;
  const snapshotDate = ledger[0]?.snapshot_date;
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Balance Control — order ledger (latest snapshot: {snapshotDate})
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Net = orders received − orders sent over the 30 Europe/Amsterdam days ending on the snapshot (approved
        commission only, distinct orders; clicks above are on UTC days). Past +band the retailer is hidden from
        partners (inbound), past −band its own pages get no overlay (outbound); release at 80% of the band. Stamps
        are decided once a day; saving a band re-stamps the latest row at once, so a change applies within a minute. A blank stamp means the row was written before Balance Control
        or by the backfill.
      </Typography>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Retailer</TableCell>
              <TableCell align="right">Orders received</TableCell>
              <TableCell align="right">Orders sent</TableCell>
              <TableCell align="right">Net orders</TableCell>
              <TableCell align="right">Paid</TableCell>
              <TableCell align="right">Earned</TableCell>
              <TableCell align="right">Net €</TableCell>
              <TableCell align="right">
                <Tooltip title="Band W in orders (retailers.balance_band_orders). Shown as 'at capture → now' when it changed since the snapshot." arrow>
                  <span>Band</span>
                </Tooltip>
              </TableCell>
              <TableCell>State</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...ledger]
              .sort((a, b) => Math.abs(b.net_30d_orders ?? 0) - Math.abs(a.net_30d_orders ?? 0))
              .map((r) => {
                const stamped = r.balance_control_enabled != null;
                const band = r.band_orders_at_capture;
                const bandNow = r.band_orders_now;
                const bandText =
                  band == null && bandNow == null ? '—' : band === bandNow ? String(band) : `${band ?? '—'} → ${bandNow ?? '—'}`;
                const net = r.net_30d_orders;
                const pastEdge = band != null && net != null && Math.abs(net) > band;
                return (
                  <TableRow key={r.retailer_id}>
                    <TableCell>{r.display_name || r.retailer_id}</TableCell>
                    <TableCell align="right">{r.orders_received_30d == null ? '—' : r.orders_received_30d.toLocaleString()}</TableCell>
                    <TableCell align="right">{r.orders_sent_30d == null ? '—' : r.orders_sent_30d.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: pastEdge ? 'warning.main' : undefined }}>
                      {net == null ? '—' : `${net > 0 ? '+' : ''}${net}`}
                    </TableCell>
                    <TableCell align="right">{eur(r.paid_30d_eur)}</TableCell>
                    <TableCell align="right">{eur(r.earned_30d_eur)}</TableCell>
                    <TableCell align="right">{eur(r.net_30d_eur)}</TableCell>
                    <TableCell align="right" sx={band == null && bandNow == null ? { color: 'text.disabled' } : undefined}>
                      {bandText}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {r.inbound_throttled && (
                          <Tooltip title="Hidden from partners' carousels from this capture until the next" arrow>
                            <Chip size="small" label="inbound throttled" color="warning" />
                          </Tooltip>
                        )}
                        {r.outbound_throttled && (
                          <Tooltip title="Own pages get no overlay from this capture until the next" arrow>
                            <Chip size="small" label="outbound throttled" color="warning" />
                          </Tooltip>
                        )}
                        {stamped && r.balance_control_enabled === false && (
                          <Tooltip title="BALANCE_CONTROL_ENABLED was off at capture: ledger kept, every throttle stamped false" arrow>
                            <Chip size="small" label="not enforced (switch off)" variant="outlined" />
                          </Tooltip>
                        )}
                        {stamped && r.balance_control_enabled && !r.inbound_throttled && !r.outbound_throttled && band != null && (
                          <Chip size="small" label="within band" variant="outlined" color="success" />
                        )}
                        {stamped && r.balance_control_enabled && !r.inbound_throttled && !r.outbound_throttled && band == null && (
                          <Tooltip title="No band on this snapshot: nothing to be within or past. Set one on the retailer's settings page." arrow>
                            <Chip size="small" label="no band" variant="outlined" sx={{ color: 'text.disabled' }} />
                          </Tooltip>
                        )}
                        {!stamped && <Chip size="small" label="no stamp" variant="outlined" sx={{ color: 'text.disabled' }} />}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const BalanceTable = ({ balance }) => {
  if (!balance?.length) return null;
  const snapshotDate = balance[0]?.snapshot_date;
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Retailer click balance (latest snapshot: {snapshotDate})
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Ranking reads exactly these values. A retailer in the deadband, under the volume gate, or in its 60-day
        grace period ranks neutral (effective score 1.0) in the fairness arm. Credits = clicks a partner&apos;s
        rule or limit removed, counted as if they had happened (Phase 2b): the score, imbalance and deadband
        include them; the volume gate uses raw clicks only.
      </Typography>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Retailer</TableCell>
              <TableCell align="right">Sent 30d</TableCell>
              <TableCell align="right">Received 30d</TableCell>
              <TableCell align="right">
                <Tooltip
                  arrow
                  title="Estimated clicks credited on each side (30d): calls a policy the retailer does NOT own removed × the retailer's own click rate on that side. Receive credit: partners' sale flags, partners' rules, a source's outbound limit. Send credit: emptied overlays on its own pages caused by a partner's flag, rule or inbound limit. A retailer's own limit is never its credit."
                >
                  <span>Credit recv / sent (est.)</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">Net-giver score</TableCell>
              <TableCell align="right">Imbalance</TableCell>
              <TableCell align="right">
                <Tooltip title="Share of receive opportunities the retailer's own sale-filter flags refused (30d, from the counterfactual rollup)" arrow>
                  <span>Refusal</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip
                  arrow
                  title={
                    <Box sx={{ maxWidth: 360 }}>
                      <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                        What this retailer's net-giver score would be if their own sale-filter flags
                        were off.
                      </Typography>
                      <Typography variant="caption" component="div">
                        1. Their real click rate: clicks received ÷ overlay appearances shown (30d).
                        <br />
                        2. Clicks their flags hid: appearances hidden by their own flags (and
                        recoverable by their flip alone) × that click rate. Assumes hidden
                        appearances get clicked like shown ones.
                        <br />
                        3. Same score formula, hidden clicks added to receives:
                        (sent + 10) ÷ (received + hidden clicks + 10) — k = 10 smoothing, identical
                        to the live score.
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        Monitoring only — never a ranking input. Distinct from the credit column,
                        which IS in the live score: this adds back the retailer&apos;s OWN refusals;
                        the credit adds back what OTHERS refused.
                      </Typography>
                    </Box>
                  }
                >
                  <span>Adj. NGS</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip
                  title="Times this shop would have been shown on an opened overlay but the size already had as many partners as the display cap allows, ranked above it (30d). For demoted net receivers this is mostly the fairness sort at work: it ranks them last, and the cap cuts the rest. Spec: overlay/match-display-cap-3.md."
                  arrow
                >
                  <span>Trimmed by cap</span>
                </Tooltip>
              </TableCell>
              <TableCell>State</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...balance]
              .sort((a, b) => b.net_giver_score - a.net_giver_score)
              .map((r) => {
                const neutral = r.in_deadband || !r.eligible || r.in_grace;
                // Self-hiding cap binds only when nothing else already neutralizes the
                // retailer AND they'd otherwise be promoted (effective_ngs = min(ngs, 1)).
                const capBinding = r.self_hiding && !neutral && r.net_giver_score > 1;
                return (
                  <TableRow key={r.retailer_id}>
                    <TableCell>{r.display_name || r.retailer_id}</TableCell>
                    <TableCell align="right">{r.sent_30d.toLocaleString()}</TableCell>
                    <TableCell align="right">{r.received_30d.toLocaleString()}</TableCell>
                    <TableCell
                      align="right"
                      sx={r.received_credit_30d == null ? { color: 'text.disabled' } : undefined}
                    >
                      {r.received_credit_30d == null ? (
                        '—'
                      ) : (
                        <Tooltip
                          arrow
                          title={`${(r.others_refused_calls_30d ?? 0).toLocaleString()} receive calls refused by others' policies; ${(r.others_emptied_calls_30d ?? 0).toLocaleString()} own-page overlays emptied by others' policies (30d)`}
                        >
                          <span>
                            +{r.received_credit_30d.toFixed(1)} / +{(r.sent_credit_30d ?? 0).toFixed(1)}
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {r.net_giver_score.toFixed(3)}
                    </TableCell>
                    <TableCell align="right">{fmtPct(r.imbalance)}</TableCell>
                    <TableCell align="right" sx={r.refusal_share == null ? { color: 'text.disabled' } : undefined}>
                      {r.refusal_share == null ? '—' : fmtPct(r.refusal_share)}
                    </TableCell>
                    <TableCell align="right" sx={r.adjusted_ngs == null ? { color: 'text.disabled' } : undefined}>
                      {r.adjusted_ngs == null ? '—' : r.adjusted_ngs.toFixed(3)}
                    </TableCell>
                    <TableCell align="right" sx={!r.trimmed_by_cap_30d ? { color: 'text.disabled' } : undefined}>
                      {r.trimmed_by_cap_30d == null ? '—' : r.trimmed_by_cap_30d.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {r.in_deadband && <Chip size="small" label="in deadband" color="default" variant="outlined" />}
                        {!r.eligible && <Chip size="small" label="under volume gate" color="warning" variant="outlined" />}
                        {r.in_grace && (
                          <Tooltip title={r.grace_ends ? `Ranks neutral until ${r.grace_ends}, then its real balance counts` : 'No first-activation date — ranks neutral until one is set'} arrow>
                            <Chip
                              size="small"
                              label={r.grace_ends ? `grace until ${r.grace_ends}` : 'grace (no activation date)'}
                              color="info"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                        {r.self_hiding && (
                          <Tooltip
                            title={`Own sale filter refused ${
                              r.snapshot_refusal_share != null ? fmtPct(r.snapshot_refusal_share) : '—'
                            } of receive opportunity; flags on ${r.hiding_days ?? '—'}/30 days${
                              capBinding ? ' — capped to neutral in fairness ranking' : ''
                            }`}
                            arrow
                          >
                            <Chip
                              size="small"
                              label={capBinding ? 'capped: self-hiding' : 'self-hiding'}
                              color={capBinding ? 'warning' : 'default'}
                              variant={capBinding ? 'filled' : 'outlined'}
                            />
                          </Tooltip>
                        )}
                        {/* A binding cap means the retailer ranks NEUTRAL — showing
                            'promoted' next to it would contradict what ranking does. */}
                        {!neutral && !capBinding && (
                          <Chip
                            size="small"
                            label={r.net_giver_score > 1 ? 'promoted' : 'demoted'}
                            color={r.net_giver_score > 1 ? 'success' : 'error'}
                          />
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const WinsTable = ({ wins }) => {
  if (!wins?.length) return null;
  const byRetailer = {};
  wins.forEach(({ retailer_id, display_name, arm, wins: n }) => {
    byRetailer[retailer_id] = byRetailer[retailer_id] || { name: display_name || retailer_id, fairness: 0, control: 0 };
    byRetailer[retailer_id][arm] = n;
  });
  const rows = Object.values(byRetailer).sort(
    (a, b) => b.fairness + b.control - (a.fairness + a.control)
  );
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Seen contested badge wins per retailer (30d)
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        As the arm ramps: out-of-band net-receivers&apos; fairness-arm wins should trend toward zero while their
        control-arm wins continue — that difference IS the lever working.
      </Typography>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Retailer</TableCell>
              <TableCell align="right">Fairness-arm wins</TableCell>
              <TableCell align="right">Control-arm wins</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((w) => (
              <TableRow key={w.name}>
                <TableCell>{w.name}</TableCell>
                <TableCell align="right">{w.fairness.toLocaleString()}</TableCell>
                <TableCell align="right">{w.control.toLocaleString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const HowToReadThis = () => (
  <Accordion disableGutters sx={{ bgcolor: 'action.hover', boxShadow: 'none', '&:before': { display: 'none' } }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        How to read this tab
      </Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Stack spacing={1.25}>
        <Typography variant="body2" color="text.secondary">
          <strong>The two arms.</strong> When 2+ price-equivalent partners can win the &quot;Our Choice&quot; badge,
          the pool is randomly assigned to one of two arms. The <strong>fairness</strong> arm ranks by net-giver
          score (send more than you receive → rank higher). The <strong>control (random)</strong> arm keeps
          today&apos;s coin-flip — a permanent baseline we measure fairness against. The split is set by
          BADGE_FAIRNESS_ARM_SHARE (currently 75%, ramped 2026-09-14; 25% stays random control).
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Net-giver score</strong> = (sent + 10) / (received + 10) over 30 days of clicks, from{' '}
          <em>all</em> handoffs (not just contested ones). Above 1.0 = net giver, below = net receiver. A retailer
          is <strong>clamped to neutral (1.0)</strong> — neither promoted nor demoted — when it&apos;s{' '}
          <em>in the deadband</em> (imbalance ≤ 25%, too balanced to act on), <em>under the volume gate</em>{' '}
          (&lt;100 clicks/30d, too little data), or <em>in its 60-day grace period</em> (too new).
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Why a grace/deadband retailer can still win a fairness pool.</strong> Clamped means neutral,
          not benched. If every partner in a pool is clamped (all at 1.0), the fairness sort has nothing to
          separate them and falls through to the random tiebreak — so any of them wins ~50%, exactly as in the
          control arm. Grace guarantees a new retailer is never <em>demoted</em>; it never promised they can&apos;t
          win a coin-flip. The arm only changes an outcome when a genuine out-of-band net-giver or net-receiver is
          in the pool.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Seen vs ranked.</strong> The composition chips count pools the ranker <em>assigned</em> to each
          arm (did the arm fire?). The performance and wins tables count only pools a shopper <em>saw</em> with 2+
          real matches after the sale filter (did it help where someone actually chose?). Right after a ramp,
          ranked runs ahead of seen until traffic accumulates.
        </Typography>
      </Stack>
    </AccordionDetails>
  </Accordion>
);

const BadgeFairnessDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await badgeFairnessApi.getSummary();
      if (result.success) {
        setSummary(result.data);
      } else {
        setError(result.error || 'Failed to load click balancing summary');
      }
    } catch (e) {
      setError(e.message || 'Failed to load click balancing summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  usePageLoadingEffect(loading);

  return (
    <Box>
      <PerformanceTabBar />
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Click balancing
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Two mechanisms on one nightly ledger. <strong>Badge Fairness</strong>: the arm-share rule for the
            &quot;Our Choice&quot; badge inside the ±5% price band — net click contributors win contested pools;
            a permanent random control arm measures the cost (ramp knob: BADGE_FAIRNESS_ARM_SHARE on
            handoff-main-v5). <strong>Balance Control</strong>: a retailer-set band in orders on its own 30-day
            net position; past the payer edge it is hidden from partners&apos; carousels, past the giver edge its own
            pages get no overlay, lifting by itself at 80% of the band (kill switch: BALANCE_CONTROL_ENABLED on
            both services; a band is set on the retailer's settings page, Retailers → retailer → Balance limit, on the retailer's request).
          </Typography>
        </Box>

        <HowToReadThis />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && summary && (
          <>
            <GuardrailRow guardrail={summary.guardrail} observedShare={summary.observed_arm_share} />
            <ArmsTable arms={summary.arms} strategyBreakdown={summary.strategy_breakdown_7d} />
            <BalanceTable balance={summary.balance} />
            <LedgerTable ledger={summary.ledger} />
            <BadgeFairnessTrends />
            <WinsTable wins={summary.wins_30d} />
          </>
        )}
      </Stack>
    </Box>
  );
};

export default BadgeFairnessDashboard;
